import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { Pencil, Trash2 } from 'lucide-react'
import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { DataTable } from '@/components/backoffice/DataTable'
import { SectionHeader } from '@/components/backoffice/SectionHeader'
import { Button } from '@/components/ui/button'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { StateWithAreas } from '@/types'

type Props = {
  country: { id: string; code: string; name: string }
  state: StateWithAreas
}

export default function StatesShow({ country, state }: Props) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [deleteAreaId, setDeleteAreaId] = useState<string | null>(null)
  const basePath = `/backoffice/countries/${country.id}/states/${state.id}`

  return (
    <BackofficeLayout title={`${state.name}, ${country.code}`} breadcrumbs={[{ label: 'Backoffice', href: '/backoffice/dashboard' }, { label: 'Jurisdictions', href: '/backoffice/countries' }, { label: country.name, href: `/backoffice/countries/${country.id}` }, { label: state.name }]}>
      <div className="space-y-6">
        <div className="mb-8">
          <h1 className="text-xl font-semibold tracking-tight">{state.name}</h1>
          <p className="text-sm text-muted-foreground">{state.code}</p>
        </div>

        <div className="flex gap-2">
          <Button size="sm" variant="outline" asChild className="h-8 text-[13px]">
            <Link href={`${basePath}/edit`}><Pencil className="mr-1.5 h-3 w-3" />Edit</Link>
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setShowDeleteDialog(true)} className="h-8 text-[13px] text-destructive hover:text-destructive">
            <Trash2 className="mr-1.5 h-3 w-3" />Delete
          </Button>
        </div>

        <div>
          <SectionHeader title="Local Areas" count={state.local_areas.length} action={{ label: 'Add Local Area', href: `${basePath}/local_areas/new` }} />
          <DataTable
            data={state.local_areas}
            keyFn={a => a.id}
            emptyMessage="No local areas added yet."
            columns={[
              { key: 'name', header: 'Name', className: 'flex-1', render: a => <span className="text-sm font-medium">{a.name}</span> },
              { key: 'code', header: 'Code', className: 'w-24', render: a => <span className="text-sm font-mono text-muted-foreground">{a.code}</span> },
              { key: 'entities', header: 'Entities', className: 'w-20', render: a => <span className="text-sm text-muted-foreground">{a.entities_count}</span> },
              { key: 'actions', header: '', className: 'w-20', render: a => (
                <div className="flex gap-0.5">
                  <Button size="icon" variant="ghost" asChild className="h-6 w-6">
                    <Link href={`${basePath}/local_areas/${a.id}/edit`}><Pencil className="h-3 w-3" /></Link>
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => setDeleteAreaId(a.id)} className="h-6 w-6">
                    <Trash2 className="h-3 w-3 text-destructive" />
                  </Button>
                </div>
              )},
            ]}
          />
        </div>

        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader><AlertDialogTitle>Delete {state.name}?</AlertDialogTitle><AlertDialogDescription>States with associated entities cannot be deleted.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => router.delete(basePath)}>Delete</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={!!deleteAreaId} onOpenChange={() => setDeleteAreaId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader><AlertDialogTitle>Delete this local area?</AlertDialogTitle><AlertDialogDescription>Local areas with associated entities cannot be deleted.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => { if (deleteAreaId) { router.delete(`${basePath}/local_areas/${deleteAreaId}`); setDeleteAreaId(null) } }}>Delete</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </BackofficeLayout>
  )
}
